// ---------------------------------------------------------------------------
// Per-floor filtering for the "stock from CAD" panel.
//
// A task's locations are what drive which floors' take-off materials to show:
// resolve EVERY assigned zone to its floor via the site catalog (ruling 10,
// 2026-09-09 — a task on several floors claims on all of them), then keep the
// take-off's materials on whichever storey(s) those floors map to. No floors
// (the task has no location, or the caller wants the whole-building view)
// returns every mapped material, not zero — "no floor filter" rather than
// "no materials".
//
// Pure functions — no React, no store; unit-tested in isolation.
// ---------------------------------------------------------------------------

import type { TFunction } from 'i18next';
import type { ISchedulerTask, ISiteLocations } from '@interfaces/IScheduler';
import type { ICadTakeoff, ITakeoffMaterial } from '@interfaces/ICadTakeoff';
import { fold } from '../../LogisticsPage/units';

/**
 * The distinct floors of ALL a task's assigned locations, in catalog
 * (top-down) order — a task occupying zones on several floors is scoped to
 * every one of them. Empty when the task has no locations, or none of its
 * zones is found in any floor of the catalog.
 */
export function taskFloorIds(
  task: Pick<ISchedulerTask, 'locations'>,
  catalog: ISiteLocations,
): string[] {
  const zoneIds = new Set((task.locations ?? []).map((l) => l.locationId));
  return catalog.floors
    .filter((floor) => floor.zones.some((z) => zoneIds.has(z.id)))
    .map((floor) => floor.id);
}

/**
 * Filters take-off materials down to a set of floors via the storey → floor
 * `mapping` (from `mapStoreysToFloors`) — the union of the rows on the
 * storeys those floors map to. A material whose storey has no floor mapping
 * is always excluded — its storey couldn't be reconciled with the site
 * catalog, so it has no meaningful floor to attribute it to, even under the
 * whole-building view. No `floorIds` returns every mapped material (the
 * whole-building total); a floor with no mapped storey (a bad mapping, e.g.
 * the empty map in tests) contributes nothing.
 */
export function materialsForFloors(
  takeoff: ICadTakeoff,
  mapping: Map<number, string>,
  floorIds: string[],
): ITakeoffMaterial[] {
  return takeoff.materials.filter((m) => {
    const f = mapping.get(m.storeyLocalId);
    return f !== undefined && (floorIds.length === 0 || floorIds.includes(f));
  });
}

/** The floors a banner or the element list's title names: the catalog names
 *  joined, or "the model" for a task with none. */
export function floorsLabel(floorNames: string[], t: TFunction): string {
  return floorNames.length === 0 ? t('scheduler.panel.stock.wholeModel') : floorNames.join(', ');
}

/**
 * The floors' take-off rows for one material — every element type it appears
 * as (a slab row and a wall row for the same concrete). Matched by fold, the
 * same rule the stock/delivery dialogs use. Empty for a name the floors'
 * take-off doesn't carry, including every "Not in the CAD" free-text claim.
 * Shared by `useTaskStock` (ceiling math) and the Stock tab's row (the CAD
 * hint line) so the two never drift on how a claim is matched back.
 */
export function cadRowsFor(
  materials: ITakeoffMaterial[],
  materialName: string,
): ITakeoffMaterial[] {
  const name = fold(materialName);
  return materials.filter((m) => fold(m.material) === name);
}

/**
 * The floors' distinct material NAMES — the Material dropdown's rows. One
 * entry per name (fold-deduped, first spelling kept), in take-off order; a
 * material's element types are a hint under the name, never a separate pick
 * (spec §5.2 — the site holds at most one stock per material).
 */
export function materialNamesForFloor(materials: ITakeoffMaterial[]): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const m of materials) {
    const key = fold(m.material);
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(m.material);
  }
  return names;
}
